import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Trash2, TriangleAlert, X } from 'lucide-react';
import { ObservableLevelEnumValue } from '../../data/enums';
import { observationService } from '../../services/observationService';
import { AddImage } from '../molecules/AddImage';
import { ScreenSkeleton } from './ScreenSkeleton';
import { Checkbox } from './BaseCheckbox';
import { Label, LabelProps } from './Label';
import { v4 } from 'uuid';
import Modal from '../molecules/Modal';
import ZoomableImage from '../molecules/ZoomableImage';
import { LoadingIndicator } from './LoadingIndicator';
import { InspectionModel } from '../../data/types';
import { useInspectionCached } from '../../hooks/useInspectionCached';
import { cacheInvalidationService } from '../../services/cacheInvalidationService';
import { ImageUtil, ImageTooLargeError } from '../../utils/image.util';
import observationImagesHelpers from '../../helpers/observationImagesHelpers';
import { toast } from '../../store/toast';
import { EToastType } from '../molecules/ToastMessage';

type ProcessingStatus = 'queued' | 'converting' | 'compressing' | 'error';

interface ProcessingItem {
  localId: string;
  fileName: string;
  status: ProcessingStatus;
  progress: number;
  errorMessage?: string;
  /** Seed time for the stuck-tile sweep (a tile queued >30s is a hung await, not feedback). */
  seededAt?: number;
}

// Error tiles carry no persisted observation (they fail before save), so they
// auto-dismiss after this delay rather than lingering in the grid.
const AUTO_DISMISS_ERROR_MS = 4000;

interface PhotoSelectorProps {
  title?: string;
  compressImage?: (file: File) => Promise<Blob>;
  path: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
  isMultiple?: boolean;
  labelProps?: Omit<LabelProps, 'tooltip' | 'children'>;
  primaryLocationId?: number;
}

const PhotoSelector: React.FC<PhotoSelectorProps> = ({
  title = 'Photos',
  compressImage,
  path,
  dataLevel,
  entityId,
  entityExternalId,
  inspectionId,
  inspectionCategoryTypeId,
  isMultiple = true,
  labelProps,
  primaryLocationId,
}) => {
  const [selectedPhotos, setSelectedPhotos] = useState<{ id: string }[]>([]);
  const [isResolvingFullImage, setIsResolvingFullImage] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<string>>(
    new Set(),
  );
  const [addImageKey, setAddImageKey] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    id: string;
    url: string;
  }>();
  const [processingItems, setProcessingItems] = useState<ProcessingItem[]>([]);
  // Cancellation state lives in refs because the sequential processing loop
  // closes over stale state.
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const cancelledIdsRef = useRef<Set<string>>(new Set());
  const cancelBatchRef = useRef(false);
  const errorTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const gridRef = useRef<HTMLDivElement>(null);
  const prevProcessingCountRef = useRef(0);

  // const { externalId } = useParams();

  const { inspection } = useInspectionCached(inspectionId);

  useEffect(() => {
    loadPhotosFromService();
  }, []);

  // Newly selected images append to the bottom of the grid — scroll there so the
  // user sees their images being added (only when new items are queued, not on load).
  useEffect(() => {
    if (processingItems.length > prevProcessingCountRef.current) {
      gridRef.current?.scrollTo({
        top: gridRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
    prevProcessingCountRef.current = processingItems.length;
  }, [processingItems.length]);

  useEffect(() => {
    const controllers = abortControllersRef.current;
    const errorTimers = errorTimersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      errorTimers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Self-heal orphaned processing tiles: a non-error tile whose AbortController is gone is a
  // finished (or dead) job whose removeProcessingItem state update never applied - observed
  // live as "Queued" tiles spinning forever while the photos were already cached, saved as
  // observations and synced. Sweep them after a grace period instead of leaving zombie tiles.
  useEffect(() => {
    if (!processingItems.some((item) => item.status !== 'error')) return;
    const sweep = setInterval(() => {
      // Only sweep when NO item holds an active controller: mid-batch, exactly one item is
      // processing (has a controller) and the rest legitimately wait without one - never
      // sweep those. No controllers at all means the loop has finished (or died), so any
      // remaining non-error tile is a zombie.
      const now = Date.now();
      setProcessingItems((prev) =>
        prev.filter((item) => {
          if (item.status === 'error') return true;
          // Loop idle (no controllers) -> any remaining non-error tile is a zombie.
          if (abortControllersRef.current.size === 0) return false;
          // Loop busy: keep normal tiles, but force-clear anything stuck beyond 30s -
          // a tile that old is not feedback, it is a hung await (abort its controller
          // so the loop can move on to the rest of the batch).
          if (now - (item.seededAt ?? now) > 30000) {
            abortControllersRef.current.get(item.localId)?.abort();
            abortControllersRef.current.delete(item.localId);
            return false;
          }
          return true;
        }),
      );
    }, 4000);
    return () => clearInterval(sweep);
  }, [processingItems]);

  const updateProcessingItem = (
    localId: string,
    patch: Partial<ProcessingItem>,
  ) => {
    setProcessingItems((prev) =>
      prev.map((item) =>
        item.localId === localId ? { ...item, ...patch } : item,
      ),
    );
  };

  const removeProcessingItem = (localId: string) => {
    setProcessingItems((prev) =>
      prev.filter((item) => item.localId !== localId),
    );
  };

  const markProcessingItemError = (localId: string, errorMessage: string) => {
    updateProcessingItem(localId, { status: 'error', errorMessage });
    const timer = setTimeout(() => {
      errorTimersRef.current.delete(localId);
      removeProcessingItem(localId);
    }, AUTO_DISMISS_ERROR_MS);
    errorTimersRef.current.set(localId, timer);
  };

  const loadPhotosFromService = async () => {
    try {
      const observations = await observationService.getObservationsByInspection(
        {
          dataLevel,
          entityId,
          entityExternalId,
          inspectionId,
          inspectionCategoryId: inspectionCategoryTypeId,
          path: path,
        },
      );

      if (observations && observations.length > 0) {
        const photos = observations
          .map((observation) => observation.value)
          .filter((photoId): photoId is string => !!photoId)
          .filter(
            (photoId) =>
              primaryLocationId === undefined ||
              photoId.startsWith(`__${primaryLocationId}__`),
          )
          .map((photoId) => ({ id: photoId }));
        setSelectedPhotos(photos);
      }
    } catch (error) {
      console.error('Error loading photos from service:', error);
    }
  };

  const savePhotoKey = async (photoId: string) => {
    try {
      await observationService.upsertObservation({
        dataLevel,
        entityId,
        entityExternalId,
        inspectionCategoryId: inspectionCategoryTypeId,
        inspectionId,
        path: path,
        value: photoId,
      });

      // Do NOT await the invalidation: invalidateQueries resolves only after every active
      // query under the key has REFETCHED, and on heavy inspection screens that can take
      // arbitrarily long - it froze this processing loop AFTER the photo was already saved
      // (stuck 'Queued' tiles, and every later photo in the batch blocked behind it). The
      // debounced helper also batches bursts of photo saves into one refresh.
      cacheInvalidationService.invalidateInspectionAfterObservationChange(
        inspectionId,
      );
    } catch (error) {
      console.error('Error saving photo key:', error);
    }
  };

  const cancelProcessingItem = (localId: string) => {
    abortControllersRef.current.get(localId)?.abort();
    abortControllersRef.current.delete(localId);
    const timer = errorTimersRef.current.get(localId);
    if (timer) {
      clearTimeout(timer);
      errorTimersRef.current.delete(localId);
    }
    cancelledIdsRef.current.add(localId);
    removeProcessingItem(localId);
  };

  const cancelRemaining = () => {
    cancelBatchRef.current = true;
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
    // Mark each cancelled item's id so an in-flight loop skips it even if a new
    // selection resets the shared batch flag before the loop reaches it.
    processingItems.forEach((item) => {
      if (item.status !== 'error') {
        cancelledIdsRef.current.add(item.localId);
      }
    });
    // Drop queued / in-progress tiles; keep error tiles (terminal) and completed photos.
    setProcessingItems((prev) =>
      prev.filter((item) => item.status === 'error'),
    );
  };

  const handleFileSelect = async (images: File[] = []) => {
    // If not multiple, only process the first file.
    const files = isMultiple ? [...images] : images.slice(0, 1);
    if (files.length === 0) return;

    // Fresh batch — clear the stop flag (cancelledIds use unique ids so they never clash).
    cancelBatchRef.current = false;
    const maxSizeMb = ImageUtil.getMaxImageSizeMegabytes();

    // Capture the existing single photo IDs now but defer deletion until replacement succeeds.
    const photoIdsToReplace =
      !isMultiple && selectedPhotos.length > 0
        ? selectedPhotos.map((p) => p.id)
        : [];

    // Seed a queued tile per file.
    const batch = files.map((file) => ({ localId: v4(), file }));
    setProcessingItems((prev) => [
      ...prev,
      ...batch.map(({ localId, file }) => ({
        localId,
        fileName: file.name,
        status: 'queued' as ProcessingStatus,
        progress: 0,
        seededAt: Date.now(),
      })),
    ]);

    let anySucceeded = false;

    for (const { localId, file } of batch) {
      if (cancelBatchRef.current || cancelledIdsRef.current.has(localId)) {
        removeProcessingItem(localId);
        continue;
      }

      const controller = new AbortController();
      abortControllersRef.current.set(localId, controller);

      try {
        // Convert HEIC first so we can accurately check the size.
        let sourceFile: File;
        if (await ImageUtil.isHEICImage(file)) {
          updateProcessingItem(localId, { status: 'converting' });
          sourceFile = await ImageUtil.convertHEICToJPEG(file);
        } else {
          sourceFile = file;
        }

        let processedFile: File;
        if (ImageUtil.shouldCompressForUpload(sourceFile)) {
          updateProcessingItem(localId, { status: 'compressing', progress: 0 });
          processedFile = await ImageUtil.compressImageForUpload(sourceFile, {
            signal: controller.signal,
            onProgress: (p) =>
              updateProcessingItem(localId, { progress: Math.round(p) }),
          });
        } else {
          processedFile = sourceFile;
        }

        const processedBlob = compressImage
          ? await compressImage(processedFile)
          : processedFile;

        // Defensive: the ladder guarantees this, but guard against the optional
        // compressImage prop producing something oversized.
        if (ImageUtil.isImageAboveMaxSize(processedBlob)) {
          throw new ImageTooLargeError(
            `Unable to compress "${file.name}" below ${maxSizeMb} MB`,
          );
        }

        const extension =
          processedBlob.type === 'image/jpeg'
            ? 'jpeg'
            : processedFile.name.split('.').pop();

        const guid = v4();
        const photoId = `${primaryLocationId !== undefined ? '__' + primaryLocationId + '__' : ''}${guid}.${extension}`;

        // Store in cache
        const cache = await caches.open('photos');
        await cache.put(photoId, new Response(processedBlob));

        // Save to service as individual observation
        await savePhotoKey(photoId);

        // The tile resolves its own thumbnail from the cached full image.
        setSelectedPhotos((prev) => [...prev, { id: photoId }]);
        removeProcessingItem(localId);
        anySucceeded = true;
      } catch (error) {
        if (controller.signal.aborted) {
          // Cancelled by the user (per-tile X or Cancel remaining).
          removeProcessingItem(localId);
        } else if (error instanceof ImageTooLargeError) {
          markProcessingItemError(
            localId,
            `Couldn't get "${file.name}" under ${maxSizeMb} MB`,
          );
          toast.add({
            type: EToastType.warning,
            message: `Couldn't get "${file.name}" under ${maxSizeMb} MB`,
            isOpen: true,
          });
        } else {
          console.error('Error processing image:', error);
          markProcessingItemError(localId, `"${file.name}" could not be added`);
        }
      } finally {
        abortControllersRef.current.delete(localId);
      }
    }

    // Deterministic backstop for the known "removeProcessingItem update never applied"
    // bug (see the self-heal sweep above): once the batch loop has finished, no tile
    // from THIS batch should still be processing. Clear any survivors now - keeping
    // error tiles for their auto-dismiss timer - instead of waiting on the 4 s sweep,
    // which is heavily throttled (and can stall to ~60 s) when the tab is backgrounded.
    const batchIds = new Set(batch.map((b) => b.localId));
    setProcessingItems((prev) =>
      prev.filter(
        (item) => item.status === 'error' || !batchIds.has(item.localId),
      ),
    );

    // Only delete the previous single photo once a replacement has been saved successfully.
    if (anySucceeded && photoIdsToReplace.length > 0) {
      await deletePhotosByIds(photoIdsToReplace);
      setSelectedForDeletion(new Set());
    }

    setAddImageKey((k) => k + 1);
  };

  const deletePhotosByIds = async (ids: string[]) => {
    if (ids.length === 0) return;

    try {
      const observations = await observationService.getObservationsByInspection(
        {
          dataLevel,
          entityId,
          entityExternalId,
          inspectionId,
          inspectionCategoryId: inspectionCategoryTypeId,
          path: path,
        },
      );

      const { deletedIds, failedIds } =
        await observationImagesHelpers.deletePhotos(
          ids,
          observations,
          (externalId) =>
            observationService.deleteObservation(
              inspectionId,
              inspectionCategoryTypeId,
              externalId,
            ),
        );

      // Do NOT await the invalidation: invalidateQueries resolves only after every active
      // query under the key has REFETCHED, and on heavy inspection screens that can take
      // arbitrarily long - it froze this processing loop AFTER the photo was already saved
      // (stuck 'Queued' tiles, and every later photo in the batch blocked behind it). The
      // debounced helper also batches bursts of photo saves into one refresh.
      cacheInvalidationService.invalidateInspectionAfterObservationChange(
        inspectionId,
      );

      const deletedIdSet = new Set(deletedIds);
      setSelectedPhotos((prev) =>
        prev.filter((photo) => !deletedIdSet.has(photo.id)),
      );

      if (failedIds.length > 0) {
        toast.add({
          type: EToastType.alert,
          message:
            failedIds.length === 1
              ? "A photo couldn't be deleted. Try again."
              : `${failedIds.length} photos couldn't be deleted. Try again.`,
          isOpen: true,
        });
      }
    } catch (error) {
      console.error('Error deleting photos:', error);
      toast.add({
        type: EToastType.alert,
        message: "Photos couldn't be deleted. Try again.",
        isOpen: true,
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedForDeletion.size === 0) return;
    await deletePhotosByIds(Array.from(selectedForDeletion));
    setSelectedForDeletion(new Set());
  };

  const togglePhotoSelection = (id: string) => {
    setSelectedForDeletion((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getGridStyle = (photoCount: number) => {
    // gap-y-8 gives each row extra top spacing; rows flow to the tile height
    // rather than being squeezed into a fixed container height.
    const base = 'grid grid-cols-3 gap-x-4 gap-y-8';
    if (photoCount === 0) {
      return `${base} h-1`;
    }
    if (photoCount <= 6) {
      return base;
    }
    return `${base} max-h-96 overflow-y-auto`;
  };

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(undefined);
  };

  useEffect(() => {
    if (selectedImage) {
      openModal();
    }
    // The zoom modal shows the full image via an object URL we created on tap.
    // Revoke it whenever selectedImage changes/clears or the component unmounts.
    return () => {
      if (selectedImage?.url) {
        URL.revokeObjectURL(selectedImage.url);
      }
    };
  }, [selectedImage]);

  const onTappedImage = async (photoId: string) => {
    // Guard against concurrent taps while a full image is still resolving.
    if (isResolvingFullImage) return;
    try {
      setIsResolvingFullImage(true);
      // Grid tiles show a low-res thumbnail; resolve the full image for the zoom view.
      const observation = inspection?.inspection?.inspectionCategoryTypes
        .flatMap((category) => category.observations)
        .find((obs) => obs.value === photoId);

      const blob = await observationImagesHelpers.getFullImageBlob(
        photoId,
        observation?.id,
      );
      if (blob) {
        setSelectedImage({ id: photoId, url: URL.createObjectURL(blob) });
      }
    } catch (e) {
      console.log(`error image tapped: ${e}`);
    } finally {
      setIsResolvingFullImage(false);
    }
  };

  const ImageReviewer = useMemo(
    () =>
      selectedImage?.url ? (
        <ZoomableImage
          className="size-full rounded-sm"
          src={selectedImage.url}
        />
      ) : undefined,
    [selectedImage],
  );

  if (!inspection) {
    // Layout stability: reserve the photos-section shape instead of a bare spinner.
    return <ScreenSkeleton heading={false} fields={2} />;
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        <Label titleType="fieldTitle" {...labelProps} tooltipKey={'photos'}>
          {title}
          {isMultiple ? ` (${selectedPhotos.length})` : ''}
        </Label>
      </div>

      <div
        ref={gridRef}
        className={`${getGridStyle(selectedPhotos.length + processingItems.length)} mt-4 mb-6 place-items-center`}
      >
        {selectedPhotos.map((photo) => (
          <div
            key={photo.id}
            className="relative size-32 cursor-pointer hover:bg-brandLight/30"
            onClick={() => onTappedImage(photo.id)}
          >
            <Checkbox
              className="absolute top-0 left-0 checked:bg-brand"
              name={`checkBoxImage${photo.id}`}
              checked={selectedForDeletion.has(photo.id)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => togglePhotoSelection(photo.id)}
            />
            <PhotoSelectorImage
              id={photo.id}
              inspection={inspection.inspection}
            />
          </div>
        ))}

        <AnimatePresence>
          {processingItems.map((item) => (
            <motion.div
              key={item.localId}
              initial={{ opacity: 0 }}
              animate={{ opacity: item.status === 'queued' ? 0.6 : 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative size-32 overflow-hidden rounded-sm border border-line bg-gray-100"
            >
              <button
                type="button"
                onClick={() => cancelProcessingItem(item.localId)}
                className="absolute top-1 right-1 z-10 rounded-full bg-black/40 p-0.5 text-white hover:bg-black/60"
                aria-label={item.status === 'error' ? 'Dismiss' : 'Cancel'}
              >
                <X size={16} />
              </button>

              {item.status === 'error' ? (
                <div className="flex h-full flex-col items-center justify-center gap-1 px-2 text-center">
                  <TriangleAlert className="text-red-500" size={20} />
                  <span className="text-[10px] leading-tight text-red-600">
                    {item.errorMessage}
                  </span>
                  {/* Depleting bar (right edge recedes left) counting down to auto-dismiss. */}
                  <div className="absolute bottom-0 left-0 h-1.5 w-full bg-red-200">
                    <motion.div
                      className="h-full bg-red-500"
                      initial={{ width: '100%' }}
                      animate={{ width: '0%' }}
                      transition={{
                        duration: AUTO_DISMISS_ERROR_MS / 1000,
                        ease: 'linear',
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2 px-2 text-center">
                  <LoadingIndicator isLoading type="medium" />
                  <span className="text-[10px] leading-tight text-inkMuted">
                    {item.status === 'queued' && 'Queued'}
                    {item.status === 'converting' && 'Converting…'}
                    {item.status === 'compressing' &&
                      `Compressing… ${item.progress}%`}
                  </span>
                  {item.status === 'compressing' && (
                    <div className="absolute bottom-0 left-0 h-1.5 w-full bg-brandLight">
                      <div
                        className="h-1.5 bg-brand transition-all duration-300"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {processingItems.some((item) => item.status !== 'error') && (
        <div className="flex gap-4 pb-4">
          <button
            type="button"
            onClick={cancelRemaining}
            className="flex w-full items-center justify-center gap-2 rounded-sm bg-brandLight/20 px-4 py-2 text-ink hover:bg-brandLight/40"
          >
            <X size={20} />
            Cancel remaining
          </button>
        </div>
      )}

      {selectedForDeletion.size > 0 && (
        <div className="flex gap-4 py-8">
          <button
            type="button"
            onClick={handleDeleteSelected}
            className="flex w-full items-center justify-center gap-2 rounded-sm bg-red-500 px-4 py-2 text-white hover:bg-red-600"
          >
            <Trash2 size={20} />
            Delete Selected
          </button>
        </div>
      )}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {ImageReviewer}
      </Modal>

      <div className="pt-4"></div>

      {(isMultiple || selectedPhotos.length === 0) && (
        <AddImage
          key={addImageKey}
          allowSelectMultiple={isMultiple}
          name="photoSelector"
          onSelectedImages={handleFileSelect}
        />
      )}
    </div>
  );
};

const PhotoSelectorImage = ({
  id,
  inspection,
}: {
  id: string;
  inspection: InspectionModel | null;
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Depend on the primitive observation id, not the whole inspection object —
  // its reference can change on any re-render, which would otherwise re-run the
  // effect for every tile and cause flashing / wasted thumbnail work.
  const observationId = useMemo(
    () =>
      inspection?.inspectionCategoryTypes
        .flatMap((category) => category.observations)
        .find((obs) => obs.value === id)?.id,
    [inspection, id],
  );

  useEffect(() => {
    // Grid tiles render a small thumbnail (get-or-create), not the full image —
    // decoding full-resolution images for 128px tiles is what makes the grid jank.
    let ownedUrl: string | null = null;
    let cancelled = false;

    const loadThumbnail = async () => {
      try {
        setLoading(true);
        setError(null);

        const thumbUrl = await observationImagesHelpers.getThumbUrl(id, () =>
          observationImagesHelpers.getFullImageBlob(id, observationId),
        );

        if (cancelled) {
          if (thumbUrl) URL.revokeObjectURL(thumbUrl);
          return;
        }

        if (thumbUrl) {
          ownedUrl = thumbUrl;
          setImageUrl(thumbUrl);
        } else {
          setError('Error displaying image');
        }
      } catch (e) {
        console.log(`error loading thumbnail: ${e}`);
        if (!cancelled) setError('Error displaying image');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadThumbnail();

    return () => {
      cancelled = true;
      if (ownedUrl) URL.revokeObjectURL(ownedUrl);
    };
  }, [id, observationId]);

  if (error) {
    return (
      <div className="absolute top-1/2 left-1/2">
        <TriangleAlert />
      </div>
    );
  }

  if (loading) {
    return <LoadingIndicator isLoading center type="medium" />;
  }

  if (!imageUrl) {
    return (
      <div className="absolute top-1/2 left-1/2">
        <TriangleAlert />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt="Selected photo"
      loading="lazy"
      decoding="async"
      className="size-full rounded-sm object-cover"
    />
  );
};

export default PhotoSelector;
